import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Mail } from "lucide-react";
import { API } from "@/lib/api";
import Logo from "@/components/Logo";

/**
 * Public modal. User submits their email; if it exists we email a reset link.
 * Server always returns 200 to avoid email enumeration.
 */
export default function ForgotPassword({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reset link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="hbt-card p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="forgot-password-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="mb-3"><Logo /></div>
            <div className="hbt-label flex items-center gap-2"><Mail className="w-3 h-3" /> Password reset</div>
            <div className="font-display text-2xl font-semibold mt-1">Forgot your password?</div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-black" data-testid="forgot-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {sent ? (
          <div data-testid="forgot-sent-message">
            <p className="text-sm text-neutral-600">
              If <span className="font-semibold text-black">{email}</span> is registered with HBT, we&apos;ve sent a reset link.
              It expires in 1 hour.
            </p>
            <p className="text-xs text-neutral-500 mt-3">Check your spam folder if you don&apos;t see it in a few minutes.</p>
            <button onClick={onClose} className="btn-primary w-full mt-6" data-testid="forgot-done-btn">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <p className="text-sm text-neutral-600">
              Enter the email associated with your HBT account and we&apos;ll send you a secure reset link.
            </p>
            <div>
              <label className="hbt-label block mb-2">Email</label>
              <input
                type="email" required autoFocus
                className="hbt-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
              <button type="submit" disabled={loading || !email} className="btn-primary" data-testid="forgot-submit-btn">
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
